import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";

export interface AgentVerdict {
  id: string;
  company_id: string;
  rep_name: string;
  validation_run: string;
  claim: string;
  claim_category: string;
  search_query: string | null;
  verdict: "confirmed" | "refuted" | "inconclusive" | "error";
  confidence: number | null;
  evidence: string | null;
  source_urls: string[] | null;
  recommended_action: string | null;
  validator: string;
  reviewed_by_human: boolean;
  human_verdict: "accept" | "reject" | null;
  human_reviewed_at: string | null;
  created_at: string;
}

/**
 * Fetches the most recent agent validation verdict for a given account.
 * Returns null if no verdict exists yet.
 */
export function useAgentVerdict(companyId: string | null | undefined) {
  return useQuery({
    queryKey: ["agent-verdict", companyId],
    enabled: !!companyId,
    queryFn: async (): Promise<AgentVerdict | null> => {
      if (!companyId) return null;
      const { data, error } = await (supabase as any).rpc("list_rep_note_validations", {
        p_rep: null,
      });
      if (error) throw error;
      const matches = (data || []).filter((v: AgentVerdict) => v.company_id === companyId);
      if (matches.length === 0) return null;
      // Most-recent first
      matches.sort(
        (a: AgentVerdict, b: AgentVerdict) =>
          new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
      );
      return matches[0];
    },
  });
}

export interface RecordHumanReviewArgs {
  validationId: string;
  humanVerdict: "accept" | "reject";
}

/** Stamps human_verdict + reviewed_by_human + human_reviewed_at on a validation row. */
export function useRecordHumanReview() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async ({ validationId, humanVerdict }: RecordHumanReviewArgs) => {
      const { error } = await (supabase as any)
        .from("rep_note_validations")
        .update({
          human_verdict: humanVerdict,
          reviewed_by_human: true,
          human_reviewed_at: new Date().toISOString(),
        })
        .eq("id", validationId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["agent-verdict"] });
    },
  });
}
